import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import { safeFormatDate, convertUTCToLocal } from '../../utils/dateUtils';
import SortableTable from '../SortableTable';
import Loading from '../Loading';
import Select from 'react-select';
import { useSelectStyles } from '../../utils/selectStyles';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
} from 'chart.js';

// Enregistrer les composants Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement
);

const GIStatsModal = ({ isOpen, onClose, startDate, endDate, fetchWithAuth, surveys, followups }) => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDayFilter, setSelectedDayFilter] = useState(null);
  const selectStyles = useSelectStyles();

  // États pour le drill-down du graphique
  const [chartView, setChartView] = useState('daily'); // 'daily' ou 'hourly'
  const [selectedDate, setSelectedDate] = useState(null);

  // Gérer le scroll de la page en arrière-plan et les raccourcis clavier
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isOpen) return;

      // Fermer avec Échap
      if (event.key === 'Escape') {
        onClose();
        return;
      }
    };

    if (isOpen) {
      // Désactiver le scroll de la page
      document.body.style.overflow = 'hidden';
      // Ajouter l'écouteur d'événements clavier
      document.addEventListener('keydown', handleKeyDown);
    } else {
      // Réactiver le scroll de la page
      document.body.style.overflow = 'unset';
    }

    // Cleanup au démontage du composant
    return () => {
      document.body.style.overflow = 'unset';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Réinitialiser la vue du graphique lors de l'ouverture de la modale
  useEffect(() => {
    if (isOpen) {
      setChartView('daily');
      setSelectedDate(null);
      setSelectedDayFilter(null);
    }
  }, [isOpen]);

  // Fetch des statistiques
  useEffect(() => {
    const fetchStats = async () => {
      if (!isOpen || !startDate || !endDate) return;

      setLoading(true);
      setError(null);

      try {
        const formattedStartDate = startDate.toISOString().split('T')[0];
        const formattedEndDate = endDate.toISOString().split('T')[0];

        const response = await fetchWithAuth(
          `${API_BASE_URL}/api/dwh/gi/unanswered-surveys/stats?startDate=${formattedStartDate}&endDate=${formattedEndDate}`
        );

        if (response.ok) {
          const data = await response.json();
          setStats(data.data || []);
        } else {
          setError('Erreur lors du chargement des statistiques');
          console.error("Failed to fetch stats");
        }
      } catch (error) {
        setError('Erreur lors du chargement des statistiques');
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [isOpen, startDate, endDate, fetchWithAuth]);

  // Fonctions pour gérer le drill-down du graphique
  const handleBarClick = (event, elements) => {
    if (elements.length > 0 && chartView === 'daily') {
      const clickedIndex = elements[0].index;
      const clickedDate = followupStats.dailyActivity[clickedIndex].date;
      setSelectedDate(clickedDate);
      setSelectedDayFilter({ value: clickedDate, label: new Date(clickedDate).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) });
      setChartView('hourly');
    }
  };

  const handleBackToDaily = () => {
    setChartView('daily');
    setSelectedDate(null);
  };

  // Fonction pour gérer la sélection de jour depuis le select
  const handleDayFilterChange = (selectedOption) => {
    setSelectedDayFilter(selectedOption);
    if (selectedOption) {
      setSelectedDate(selectedOption.value);
      setChartView('hourly');
    } else {
      setSelectedDate(null);
      setChartView('daily');
    }
  };

  // Préparer les données pour la vue horaire
  const getHourlyData = () => {
    if (!selectedDate || !followupStats.hourlyActivity[selectedDate]) {
      return { labels: [], datasets: [] };
    }

    const hourlyData = followupStats.hourlyActivity[selectedDate];
    const hours = Array.from({ length: 24 }, (_, i) => i); // 0 à 23
    const hourlyTotals = hours.map(hour => hourlyData[hour]?.total || 0);

    // Calculer la moyenne horaire (total des relances / nombre d'heures avec activité)
    const hoursWithActivity = hourlyTotals.filter(total => total > 0).length;
    const totalFollowups = hourlyTotals.reduce((sum, total) => sum + total, 0);
    const averagePerActiveHour = hoursWithActivity > 0 ? totalFollowups / hoursWithActivity : 0;

    return {
      labels: hours.map(hour => `${hour.toString().padStart(2, '0')}h`),
      datasets: [
        {
          label: 'Abandonnées',
          data: hours.map(hour => hourlyData[hour]?.wontdo || 0),
          backgroundColor: 'rgba(239, 68, 68, 0.8)',
          borderColor: 'rgba(239, 68, 68, 1)',
          borderWidth: 1,
          borderRadius: {
            topLeft: 0,
            topRight: 0,
            bottomLeft: 4,
            bottomRight: 4
          },
          stack: 'stack1',
          type: 'bar'
        },
        {
          label: 'En attente',
          data: hours.map(hour => hourlyData[hour]?.pending || 0),
          backgroundColor: 'rgba(251, 191, 36, 0.8)',
          borderColor: 'rgba(251, 191, 36, 1)',
          borderWidth: 1,
          borderRadius: 0,
          stack: 'stack1',
          type: 'bar'
        },
        {
          label: 'Terminées',
          data: hours.map(hour => hourlyData[hour]?.done || 0),
          backgroundColor: 'rgba(34, 197, 94, 0.8)',
          borderColor: 'rgba(34, 197, 94, 1)',
          borderWidth: 1,
          borderRadius: {
            topLeft: 4,
            topRight: 4,
            bottomLeft: 0,
            bottomRight: 0
          },
          stack: 'stack1',
          type: 'bar'
        },
        {
          label: `Moyenne (${averagePerActiveHour.toFixed(1)} relances/h active)`,
          data: hours.map(() => averagePerActiveHour),
          type: 'line',
          borderColor: 'rgba(99, 102, 241, 1)',
          backgroundColor: 'rgba(99, 102, 241, 0.1)',
          borderWidth: 2,
          borderDash: [5, 5],
          pointRadius: 0,
          pointHoverRadius: 4,
          fill: false,
          tension: 0,
          yAxisID: 'y'
        }
      ]
    };
  };

  // Calculer les statistiques de relances
  const followupStats = React.useMemo(() => {
    if (!followups || Object.keys(followups).length === 0) {
      return {
        totalFollowups: 0,
        statusStats: { pending: 0, done: 0, wontdo: 0 },
        agentActivity: [],
        dailyActivity: [],
        dayOptions: []
      };
    }

    let totalFollowups = 0;
    const relanceurCounts = {};
    const statusCounts = { pending: 0, done: 0, wontdo: 0 };
    const agentStatusActivity = {};

    Object.values(followups).forEach(followupList => {
      followupList.forEach(followup => {
        // Filtrer par jour si un jour spécifique est sélectionné
        if (selectedDayFilter) {
          const localDate = convertUTCToLocal(followup.created_at);
          if (!localDate || localDate.toISOString().split('T')[0] !== selectedDayFilter.value) {
            return; // Ignorer ce followup s'il n'est pas du jour sélectionné
          }
        }

        totalFollowups++;
        const username = followup.username || 'Inconnu';
        const status = followup.status || 'pending';

        // Compter par relanceur
        relanceurCounts[username] = (relanceurCounts[username] || 0) + 1;

        // Compter par statut
        statusCounts[status] = (statusCounts[status] || 0) + 1;

        // Compter par agent et statut
        if (!agentStatusActivity[username]) {
          agentStatusActivity[username] = { pending: 0, done: 0, wontdo: 0, total: 0 };
        }
        agentStatusActivity[username][status] = (agentStatusActivity[username][status] || 0) + 1;
        agentStatusActivity[username].total++;
      });
    });

    const agentActivity = Object.entries(agentStatusActivity)
      .map(([username, stats]) => ({ username, ...stats }))
      .sort((a, b) => b.total - a.total);

    // Calculer les relances par jour avec statuts
    const dailyActivity = {};
    const hourlyActivity = {}; // Pour stocker les données par heure

    Object.values(followups).forEach(followupList => {
      followupList.forEach(followup => {
        // Convertir la date UTC vers la timezone locale
        const localDate = convertUTCToLocal(followup.created_at);
        if (!localDate) return;

        const date = localDate.toISOString().split('T')[0];
        const hour = localDate.getHours();
        const status = followup.status || 'pending';

        // Activité quotidienne
        if (!dailyActivity[date]) {
          dailyActivity[date] = { pending: 0, done: 0, wontdo: 0, total: 0 };
        }
        dailyActivity[date][status]++;
        dailyActivity[date].total++;

        // Activité horaire
        if (!hourlyActivity[date]) {
          hourlyActivity[date] = {};
        }
        if (!hourlyActivity[date][hour]) {
          hourlyActivity[date][hour] = { pending: 0, done: 0, wontdo: 0, total: 0 };
        }
        hourlyActivity[date][hour][status]++;
        hourlyActivity[date][hour].total++;
      });
    });

    // Convertir en array et trier par date
    const dailyActivityArray = Object.entries(dailyActivity)
      .map(([date, counts]) => ({ date, ...counts }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    // Créer les options pour le select des jours
    const dayOptions = dailyActivityArray.map(day => ({
      value: day.date,
      label: new Date(day.date).toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    }));

    return {
      totalFollowups,
      statusStats: statusCounts,
      agentActivity,
      dailyActivity: dailyActivityArray,
      hourlyActivity,
      dayOptions
    };
  }, [followups, selectedDayFilter]);

  // Enrichir les stats avec les données de relances
  const enrichedStats = React.useMemo(() => {
    return stats.map(stat => {
      // Compter les relances pour cette campagne
      const campaignFollowups = surveys
        .filter(survey => survey.CampaignId === stat.CampaignId)
        .reduce((total, survey) => total + (survey.FollowupCount || 0), 0);

      const completionRate = stat.Total > 0 ? ((stat.Answered / stat.Total) * 100).toFixed(1) : 0;

      return {
        ...stat,
        CompletionRate: parseFloat(completionRate),
        FollowupCount: campaignFollowups
      };
    });
  }, [stats, surveys]);

  // Colonnes pour le tableau des campagnes
  const campaignColumns = [
    {
      key: 'CampaignName',
      label: 'Campagne',
      render: (stat) => stat.CampaignName
    },
    {
      key: 'CampaignEnabled',
      label: 'Active',
      rawValue: (stat) => stat.CampaignEnabled ? 'Oui' : 'Non',
      render: (stat) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          stat.CampaignEnabled
            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
        }`}>
          {stat.CampaignEnabled ? 'Oui' : 'Non'}
        </span>
      )
    },
    {
      key: 'Total',
      label: 'Total envoyés',
      render: (stat) => stat.Total
    },
    {
      key: 'Answered',
      label: 'Répondus',
      render: (stat) => (
        <span className="text-green-600 dark:text-green-400 font-medium">
          {stat.Answered}
        </span>
      )
    },
    {
      key: 'Unanswered',
      label: 'Non répondus',
      render: (stat) => (
        <span className="text-red-600 dark:text-red-400 font-medium">
          {stat.Unanswered}
        </span>
      )
    },
    {
      key: 'CompletionRate',
      label: 'Taux de complétion',
      render: (stat) => (
        <div className="flex items-center">
          <div className={`px-2 py-1 rounded-full text-xs font-medium ${
            stat.CompletionRate >= 80 ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
            stat.CompletionRate >= 60 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
            'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
          }`}>
            {stat.CompletionRate}%
          </div>
        </div>
      ),
      rawValue: (stat) => stat.CompletionRate
    },
    {
      key: 'FollowupCount',
      label: 'Relances effectuées',
      render: (stat) => (
        <span className="text-blue-600 dark:text-blue-400 font-medium">
          {stat.FollowupCount}
        </span>
      )
    }
  ];

  // Colonnes pour le tableau d'activité détaillée des agents
  const agentActivityColumns = [
    {
      key: 'username',
      label: 'Agent',
      render: (agent) => (
        <span className="font-medium text-gray-900 dark:text-gray-100">
          {agent.username}
        </span>
      )
    },
    {
      key: 'total',
      label: 'Total relances',
      render: (agent) => (
        <span className="text-blue-600 dark:text-blue-400 font-bold">
          {agent.total}
        </span>
      )
    },
    {
      key: 'pending',
      label: 'En attente',
      render: (agent) => (
        <span className="text-yellow-600 dark:text-yellow-400 font-medium">
          {agent.pending}
        </span>
      )
    },
    {
      key: 'done',
      label: 'Terminées',
      render: (agent) => (
        <span className="text-green-600 dark:text-green-400 font-medium">
          {agent.done}
        </span>
      )
    },
    {
      key: 'wontdo',
      label: 'Abandonnées',
      render: (agent) => (
        <span className="text-red-600 dark:text-red-400 font-medium">
          {agent.wontdo}
        </span>
      )
    },
    {
      key: 'efficiency',
      label: 'Taux de résolution',
      render: (agent) => {
        const resolved = agent.done + agent.wontdo;
        const rate = agent.total > 0 ? ((resolved / agent.total) * 100).toFixed(1) : 0;
        return (
          <div className="flex items-center">
            <div className={`px-2 py-1 rounded-full text-xs font-medium ${
              rate >= 80 ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
              rate >= 60 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
              'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
            }`}>
              {rate}%
            </div>
          </div>
        );
      },
      rawValue: (agent) => {
        const resolved = agent.done + agent.wontdo;
        return agent.total > 0 ? ((resolved / agent.total) * 100) : 0;
      }
    }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-7xl sm:w-full">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                Statistiques des campagnes GI
              </h3>
              <button
                onClick={onClose}
                className="bg-white dark:bg-gray-800 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <span className="sr-only">Fermer</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Période */}
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                <span className="font-medium">Période analysée:</span> {safeFormatDate(startDate)} - {safeFormatDate(endDate)}
              </div>
            </div>

            {/* Filtre par jour */}
            {followupStats.dayOptions.length > 0 && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Filtrer par jour spécifique
                </label>
                <div className="max-w-md">
                  <Select
                    value={selectedDayFilter}
                    onChange={handleDayFilterChange}
                    options={followupStats.dayOptions}
                    styles={selectStyles}
                    placeholder="Toute la période"
                    isClearable
                    menuPlacement="auto"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>
                {selectedDayFilter && (
                  <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                    📊 Données filtrées pour le {selectedDayFilter.label} • {followupStats.totalFollowups} relance{followupStats.totalFollowups > 1 ? 's' : ''} ce jour-là
                  </div>
                )}
              </div>
            )}

            {loading ? (
              <div className="h-64">
                <Loading
                  size="md"
                  message="Analyse des statistiques en cours..."
                  showFunFact={true}
                  className="h-full"
                />
              </div>
            ) : error ? (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 my-4">
                <p className="text-red-700 dark:text-red-300">{error}</p>
              </div>
            ) : (
              <div className="space-y-8">
                {/* Métriques globales */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {enrichedStats.reduce((sum, stat) => sum + stat.Total, 0).toLocaleString('fr-FR')}
                    </div>
                    <div className="text-sm text-blue-800 dark:text-blue-300">
                      Formulaires envoyés{selectedDayFilter ? ` (période totale)` : ''}
                    </div>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {enrichedStats.reduce((sum, stat) => sum + stat.Answered, 0)}
                    </div>
                    <div className="text-sm text-green-800 dark:text-green-300">
                      Formulaires répondus{selectedDayFilter ? ` (période totale)` : ''} ({enrichedStats.reduce((sum, stat) => sum + stat.Total, 0) > 0 ?
                        ((enrichedStats.reduce((sum, stat) => sum + stat.Answered, 0) / enrichedStats.reduce((sum, stat) => sum + stat.Total, 0)) * 100).toFixed(1) :
                        '0'}%)
                    </div>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                      {enrichedStats.reduce((sum, stat) => sum + stat.Unanswered, 0).toLocaleString('fr-FR')}
                    </div>
                    <div className="text-sm text-red-800 dark:text-red-300">
                      Formulaires non répondus{selectedDayFilter ? ` (période totale)` : ''} ({enrichedStats.reduce((sum, stat) => sum + stat.Total, 0) > 0 ?
                        ((enrichedStats.reduce((sum, stat) => sum + stat.Unanswered, 0) / enrichedStats.reduce((sum, stat) => sum + stat.Total, 0)) * 100).toFixed(1) :
                        '0'}%)
                    </div>
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                      {followupStats.totalFollowups.toLocaleString('fr-FR')}
                    </div>
                    <div className="text-sm text-purple-800 dark:text-purple-300">
                      Relances effectuées{selectedDayFilter ? ` ce jour` : ''}
                    </div>
                  </div>
                </div>

                {/* Répartition des statuts de relances */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                    Répartition des statuts de relances{selectedDayFilter ? ` - ${selectedDayFilter.label}` : ` - Période complète`}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-xl">⏳</span>
                        <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200">En attente</span>
                      </div>
                      <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                        {followupStats.statusStats.pending.toLocaleString('fr-FR')}
                      </div>
                      <div className="text-xs text-yellow-700 dark:text-yellow-300">
                        {followupStats.totalFollowups > 0 ?
                          `${((followupStats.statusStats.pending / followupStats.totalFollowups) * 100).toFixed(1)}%` :
                          '0%'
                        }
                      </div>
                    </div>
                    <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-xl">✅</span>
                        <span className="text-sm font-medium text-green-800 dark:text-green-200">Terminées</span>
                      </div>
                      <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {followupStats.statusStats.done.toLocaleString('fr-FR')}
                      </div>
                      <div className="text-xs text-green-700 dark:text-green-300">
                        {followupStats.totalFollowups > 0 ?
                          `${((followupStats.statusStats.done / followupStats.totalFollowups) * 100).toFixed(1)}%` :
                          '0%'
                        }
                      </div>
                    </div>
                    <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-xl">❌</span>
                        <span className="text-sm font-medium text-red-800 dark:text-red-200">Abandonnées</span>
                      </div>
                      <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                        {followupStats.statusStats.wontdo.toLocaleString('fr-FR')}
                      </div>
                      <div className="text-xs text-red-700 dark:text-red-300">
                        {followupStats.totalFollowups > 0 ?
                          `${((followupStats.statusStats.wontdo / followupStats.totalFollowups) * 100).toFixed(1)}%` :
                          '0%'
                        }
                      </div>
                    </div>
                  </div>
                </div>

                {/* Histogramme des relances avec drill-down */}
                {followupStats.dailyActivity.length > 0 && !selectedDayFilter && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                        {chartView === 'daily' ? 'Activité de relance par jour' : `Activité de relance par heure - ${new Date(selectedDate).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}`}
                      </h4>

                      {chartView === 'hourly' && (
                        <button
                          onClick={handleBackToDaily}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                          </svg>
                          Retour à la vue période
                        </button>
                      )}
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                      <div className="h-80">
                        <Bar
                          data={chartView === 'daily' ? (() => {
                            // Calculer la moyenne quotidienne
                            const dailyTotals = followupStats.dailyActivity.map(day => day.total);
                            const totalFollowups = dailyTotals.reduce((sum, total) => sum + total, 0);
                            const averagePerDay = followupStats.dailyActivity.length > 0 ? totalFollowups / followupStats.dailyActivity.length : 0;

                            return {
                              labels: followupStats.dailyActivity.map(day => {
                                const date = new Date(day.date);
                                return date.toLocaleDateString('fr-FR', {
                                  day: '2-digit',
                                  month: '2-digit'
                                });
                              }),
                              datasets: [
                                {
                                  label: 'Abandonnées',
                                  data: followupStats.dailyActivity.map(day => day.wontdo),
                                  backgroundColor: 'rgba(239, 68, 68, 0.8)',
                                  borderColor: 'rgba(239, 68, 68, 1)',
                                  borderWidth: 1,
                                  borderRadius: {
                                    topLeft: 0,
                                    topRight: 0,
                                    bottomLeft: 4,
                                    bottomRight: 4
                                  },
                                  stack: 'stack1',
                                  type: 'bar'
                                },
                                {
                                  label: 'En attente',
                                  data: followupStats.dailyActivity.map(day => day.pending),
                                  backgroundColor: 'rgba(251, 191, 36, 0.8)',
                                  borderColor: 'rgba(251, 191, 36, 1)',
                                  borderWidth: 1,
                                  borderRadius: 0,
                                  stack: 'stack1',
                                  type: 'bar'
                                },
                                {
                                  label: 'Terminées',
                                  data: followupStats.dailyActivity.map(day => day.done),
                                  backgroundColor: 'rgba(34, 197, 94, 0.8)',
                                  borderColor: 'rgba(34, 197, 94, 1)',
                                  borderWidth: 1,
                                  borderRadius: {
                                    topLeft: 4,
                                    topRight: 4,
                                    bottomLeft: 0,
                                    bottomRight: 0
                                  },
                                  stack: 'stack1',
                                  type: 'bar'
                                },
                                {
                                  label: `Moyenne (${averagePerDay.toFixed(1)} relances/jour)`,
                                  data: followupStats.dailyActivity.map(() => averagePerDay),
                                  type: 'line',
                                  borderColor: 'rgba(99, 102, 241, 1)',
                                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                                  borderWidth: 2,
                                  borderDash: [5, 5],
                                  pointRadius: 0,
                                  pointHoverRadius: 4,
                                  fill: false,
                                  tension: 0,
                                  yAxisID: 'y'
                                }
                              ]
                            };
                          })() : getHourlyData()}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            onClick: handleBarClick,
                            plugins: {
                              legend: {
                                display: true,
                                position: 'top',
                                labels: {
                                  usePointStyle: true,
                                  padding: 20,
                                  color: 'rgb(107, 114, 128)',
                                  filter: function(legendItem, chartData) {
                                    // Afficher tous les éléments de légende
                                    return true;
                                  }
                                }
                              },
                              tooltip: {
                                callbacks: {
                                  title: function(context) {
                                    const index = context[0].dataIndex;
                                    if (chartView === 'daily') {
                                      const date = new Date(followupStats.dailyActivity[index].date);
                                      return date.toLocaleDateString('fr-FR', {
                                        weekday: 'long',
                                        day: 'numeric',
                                        month: 'long',
                                        year: 'numeric'
                                      });
                                    } else {
                                      const hour = index;
                                      return `${hour.toString().padStart(2, '0')}h00 - ${(hour + 1).toString().padStart(2, '0')}h00`;
                                    }
                                  },
                                  label: function(context) {
                                    const count = context.parsed.y;
                                    const datasetLabel = context.dataset.label;
                                    return `${datasetLabel}: ${count} relance${count > 1 ? 's' : ''}`;
                                  },
                                  afterBody: function(context) {
                                    const index = context[0].dataIndex;
                                    const datasetIndex = context[0].datasetIndex;

                                    // Ne pas afficher d'info supplémentaire pour la ligne de moyenne
                                    if (context[0].dataset.type === 'line') {
                                      return null;
                                    }

                                    if (chartView === 'daily') {
                                      const dayData = followupStats.dailyActivity[index];
                                      return `Total: ${dayData.total} relance${dayData.total > 1 ? 's' : ''} • Cliquez pour voir le détail horaire`;
                                    } else {
                                      const hourData = followupStats.hourlyActivity[selectedDate][index];
                                      if (hourData) {
                                        return `Total: ${hourData.total} relance${hourData.total > 1 ? 's' : ''}`;
                                      }
                                      return 'Aucune relance';
                                    }
                                  }
                                }
                              }
                            },
                            scales: {
                              y: {
                                beginAtZero: true,
                                stacked: true,
                                ticks: {
                                  stepSize: 1,
                                  color: 'rgb(107, 114, 128)'
                                },
                                grid: {
                                  color: 'rgba(107, 114, 128, 0.1)'
                                }
                              },
                              x: {
                                stacked: true,
                                ticks: {
                                  color: 'rgb(107, 114, 128)',
                                  maxRotation: chartView === 'hourly' ? 45 : 45
                                },
                                grid: {
                                  display: false
                                }
                              }
                            }
                          }}
                        />
                      </div>

                      {/* Légende */}
                      <div className="mt-4 text-center">
                        <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                          {chartView === 'daily' ? (
                            <>Activité de relance sur {followupStats.dailyActivity.length} jour{followupStats.dailyActivity.length > 1 ? 's' : ''} • Cliquez sur une barre pour voir le détail horaire</>
                          ) : (
                            <>Activité de relance par heure pour le {new Date(selectedDate).toLocaleDateString('fr-FR')}</>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          {chartView === 'daily' ? (
                            <>Maximum: {Math.max(...followupStats.dailyActivity.map(d => d.total))} relances • Total: {followupStats.totalFollowups} relances</>
                          ) : (
                            <>Total du jour: {followupStats.dailyActivity.find(d => d.date === selectedDate)?.total || 0} relances</>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Vue horaire pour le jour sélectionné */}
                {selectedDayFilter && followupStats.hourlyActivity[selectedDayFilter.value] && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                        Activité de relance par heure - {selectedDayFilter.label}
                      </h4>
                      <button
                        onClick={() => handleDayFilterChange(null)}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Retour à la vue période
                      </button>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                      <div className="h-80">
                        <Bar
                          data={getHourlyData()}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                display: true,
                                position: 'top',
                                labels: {
                                  usePointStyle: true,
                                  padding: 20,
                                  color: 'rgb(107, 114, 128)',
                                  filter: function(legendItem, chartData) {
                                    return true;
                                  }
                                }
                              },
                              tooltip: {
                                callbacks: {
                                  title: function(context) {
                                    const hour = context[0].dataIndex;
                                    return `${hour.toString().padStart(2, '0')}h00 - ${(hour + 1).toString().padStart(2, '0')}h00`;
                                  },
                                  label: function(context) {
                                    const count = context.parsed.y;
                                    const datasetLabel = context.dataset.label;
                                    return `${datasetLabel}: ${count} relance${count > 1 ? 's' : ''}`;
                                  },
                                  afterBody: function(context) {
                                    if (context[0].dataset.type === 'line') {
                                      return null;
                                    }
                                    const hour = context[0].dataIndex;
                                    const hourData = followupStats.hourlyActivity[selectedDayFilter.value][hour];
                                    if (hourData) {
                                      return `Total: ${hourData.total} relance${hourData.total > 1 ? 's' : ''}`;
                                    }
                                    return 'Aucune relance';
                                  }
                                }
                              }
                            },
                            scales: {
                              y: {
                                beginAtZero: true,
                                stacked: true,
                                ticks: {
                                  stepSize: 1,
                                  color: 'rgb(107, 114, 128)'
                                },
                                grid: {
                                  color: 'rgba(107, 114, 128, 0.1)'
                                }
                              },
                              x: {
                                stacked: true,
                                ticks: {
                                  color: 'rgb(107, 114, 128)',
                                  maxRotation: 45
                                },
                                grid: {
                                  display: false
                                }
                              }
                            }
                          }}
                        />
                      </div>
                      <div className="mt-4 text-center">
                        <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                          Activité de relance par heure pour le {selectedDayFilter.label}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          Total du jour: {followupStats.dailyActivity.find(d => d.date === selectedDayFilter.value)?.total || 0} relances
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tableau des campagnes */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                    Détail par campagne{selectedDayFilter ? ` (période totale)` : ''}
                  </h4>
                  <div className="h-64">
                    <SortableTable
                      data={enrichedStats}
                      columns={campaignColumns}
                      emptyMessage="Aucune statistique disponible"
                      countLabel={`${enrichedStats.length} campagne${enrichedStats.length > 1 ? 's' : ''}`}
                      exportable={true}
                      exportFilename="statistiques-campagnes.csv"
                      initialSortBy={{ key: 'CompletionRate', direction: 'ascending' }}
                    />
                  </div>
                </div>

                {/* Activité détaillée des agents */}
                {followupStats.agentActivity.length > 0 && (
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                      Activité détaillée des agents{selectedDayFilter ? ` - ${selectedDayFilter.label}` : ` - Période complète`}
                    </h4>
                    <div className="h-64">
                      <SortableTable
                        data={followupStats.agentActivity}
                        columns={agentActivityColumns}
                        emptyMessage="Aucune activité d'agent"
                        countLabel={`${followupStats.agentActivity.length} agent${followupStats.agentActivity.length > 1 ? 's' : ''}`}
                        exportable={true}
                        exportFilename="activite-agents.csv"
                        initialSortBy={{ key: 'total', direction: 'descending' }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GIStatsModal;